import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { Button, Dropdown, Menu, Select, Modal, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { StopPropagation } from '@core/ui';
import { tableColumns } from '@core/table';
import { filterOption } from '@core/form';
import { useService } from '@core/inversify-react';
import { useBus } from '@core/event-bus';

import { IBranchInspection } from '../../interfaces';
import { branchInspectionQueryKeys } from '../../utils';
import { useAuth } from '@modules/auth';
import { useBranches } from '@modules/branches';
import { useStatusByModelId } from '@modules/status';
import { ChangeInspectionStatusRepoType, IChangeInspectionStatusRepo } from '../../repos';

export const useBranchInspectionsTableColumns = () => {
  const history = useHistory();
  const location = useLocation();
  const { can } = useAuth();
  const { publish } = useBus();

  const branches = useBranches();
  const status = useStatusByModelId('51');
  const changeInspectionStatusRepo = useService<IChangeInspectionStatusRepo>(ChangeInspectionStatusRepoType);

  const actionsColumn = useMemo<Column<IBranchInspection>>(
    () => ({
      ...tableColumns.actions,
      Cell: ({ row: { original } }) => {
        const [visible, setVisible] = useState<boolean>(false);

        const openDetail = useCallback(() => {
          history.push(generatePath('/@next/branch-inspections/:id/details', { id: original.id }), { background: location });
        }, [original.id]);

        const openReport = useCallback(() => {
          history.push(generatePath('/@next/branch-inspections/:id/report', { id: original.id }), { background: location });
        }, [original.id]);

        const cancel = useCallback(() => {
          const onOk = async () => {
            const result = await changeInspectionStatusRepo.execute(original.id.toString(), { state_id: 158 });

            if (result.status === 200) {
              message.success(`#${original.id} nömrəli yoxlanış ləğv edildi`);
              publish({ type: '@branch-inspections/update/succeed', payload: [original.id] });
            } else {
              message.error(result.response);
            }
          };

          Modal.confirm({
            title: 'Yoxlanışı ləğv et',
            content: `#${original.id} nömrəli yoxlanışı ləğv etməyə əminsiniz?`,
            okText: 'Bəli',
            cancelText: 'Xeyr',
            okButtonProps: { danger: true },
            onOk,
          });
        }, [original.id]);

        const openScan = useCallback(() => {
          history.push(generatePath('/@next/branch-inspections/:id/scan', { id: original.id }));
        }, [original.id]);

        const canScan = [155, 156].includes(original.stateId);
        const canCancel = original.stateId !== 157;
        const canViewReport = original.stateId === 157;

        const overlay = (
          <Menu onClick={() => setVisible(false)}>
            {can('branch_inspection_scan') && (
              <Menu.Item onClick={openScan} icon={<Icons.ScanOutlined />} disabled={!canScan}>
                Yoxlanışa başla
              </Menu.Item>
            )}
            {can('branch_inspection_report') && (
              <Menu.Item onClick={openDetail} icon={<Icons.EyeOutlined />}>
                Ətraflı
              </Menu.Item>
            )}
            {can('branch_inspection_update') && (
              <Menu.Item onClick={cancel} icon={<Icons.CloseCircleOutlined />} danger disabled={!canCancel}>
                Ləğv et
              </Menu.Item>
            )}
            {can('branch_inspection_report') && (
              <Menu.Item onClick={openReport} icon={<Icons.FileTextOutlined />} disabled={!canViewReport}>
                Hesabat
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <StopPropagation>
            <Dropdown visible={visible} onVisibleChange={setVisible} overlay={overlay}>
              <Button icon={<Icons.MoreOutlined />} size='small' />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [can, history, location, publish, changeInspectionStatusRepo],
  );

  const baseColumns = useMemo<Column<IBranchInspection>[]>(
    () => [
      {
        ...tableColumns.small,
        accessor: (row) => row.id,
        id: branchInspectionQueryKeys.id,
        Header: 'Kod',
      },
      {
        ...tableColumns.large,
        accessor: (row) => row.branch?.name,
        id: branchInspectionQueryKeys.branchId,
        Header: 'Filial',
        Filter: ({ column: { filterValue, setFilter } }) => {
          return (
            <Select allowClear={true} filterOption={filterOption} showSearch style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
              {branches.data?.map((branch) => (
                <Select.Option key={branch.id} value={branch.id.toString()}>
                  {branch.name}
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.state?.name || '-',
        id: branchInspectionQueryKeys.stateId,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }) => {
          return (
            <Select allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
              {status.data?.map((state) => (
                <Select.Option key={state.id} value={state.id.toString()}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
      {
        ...tableColumns.date,
        accessor: (row) => row.deadline.value,
        id: branchInspectionQueryKeys.deadline,
        Header: 'Son tarix',
      },
      {
        ...tableColumns.date,
        accessor: (row) => row.startedAt,
        id: branchInspectionQueryKeys.startedAt,
        Header: 'Başlanma tarixi',
      },
      {
        ...tableColumns.date,
        accessor: (row) => row.completedAt,
        id: branchInspectionQueryKeys.completedAt,
        Header: 'Tamamlanma tarixi',
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.createdByUser?.name || '-',
        id: branchInspectionQueryKeys.createdByUserName,
        Header: 'Yaradan',
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.executedByUser?.name || '-',
        id: branchInspectionQueryKeys.executedByUserName,
        Header: 'İcraçı',
      },
      {
        ...tableColumns.date,
        accessor: (row) => row.createdAt,
        id: branchInspectionQueryKeys.createdAt,
        Header: 'Yaradılma tarixi',
      },
    ],
    [branches, status],
  );

  return useMemo(() => {
    return [actionsColumn, ...baseColumns];
  }, [actionsColumn, baseColumns]);
};
